import { RectangleShape, type AbstractCanvas2D } from '@maxgraph/core';

/**
 * Custom shape that renders a left-side bracket with a top label.
 * Used as the container shape for cycle visualization.
 *
 * Visual: open left bracket — no right/bottom border.
 * The interior is transparent so child cells show through.
 *
 * Reads `bracketLabel` and `depthOffset` from the **cell value** object
 * (set during insertVertex). `strokeColor` and `strokeWidth` come from
 * the standard CellStyle.
 */
export class CycleBracketShape extends RectangleShape {
  paintBackground(
    _c: AbstractCanvas2D,
    _x: number,
    _y: number,
    _w: number,
    _h: number,
  ): void {
    // No fill — transparent interior
  }

  paintForeground(
    c: AbstractCanvas2D,
    x: number,
    y: number,
    w: number,
    h: number,
  ): void {
    const style = this.style as Record<string, unknown> | undefined;
    const strokeColor = (style?.strokeColor as string) ?? '#6366F1';
    const strokeWidth = Number((style?.strokeWidth as number) ?? 2);

    // Read custom properties from cell value
    const cellValue = this.state?.cell?.getValue();
    const bracketLabel = (cellValue?.bracketLabel as string) ?? '';
    const depthOffset = Number((cellValue?.depthOffset as number) ?? 0);

    const bx = x + depthOffset;
    const bracketWidth = 10;
    const labelHeight = 18;

    c.setStrokeColor(strokeColor);
    c.setStrokeWidth(strokeWidth);
    c.setLineCap('round');
    c.setLineJoin('round');

    // ── Top horizontal tab ─────────────────────────────────────────────
    c.begin();
    c.moveTo(bx + bracketWidth, y);
    c.lineTo(bx, y);
    // ── Left vertical line ─────────────────────────────────────────────
    c.lineTo(bx, y + h);
    // ── Bottom horizontal tab ──────────────────────────────────────────
    c.lineTo(bx + bracketWidth, y + h);
    c.stroke();

    // ── Label (e.g. "↻ ×3") ───────────────────────────────────────────
    if (bracketLabel) {
      c.setFontColor(strokeColor);
      c.setFontSize(10);
      c.setFontFamily('Inter, system-ui, sans-serif');
      c.setFontStyle(1); // bold
      c.text(
        bx + bracketWidth + 4,
        y + labelHeight / 2,
        0,
        0,
        bracketLabel,
        'left',
        'middle',
        false,
        '',
        'visible',
        false,
        0,
        '',
      );
    }
  }
}
